import React, {createContext, useContext, useMemo, useState} from 'react'
import {useLocation} from 'react-router'

export type TIdeationTab = 'setup' | 'results' | 'insights'

interface IEmbeddedIdeationScope {
  ideationId: string
  activeTab: TIdeationTab
  setActiveTab: (tab: TIdeationTab) => void
}

interface IIdeationScope {
  ideationId: string
  /** Set when the screens are hosted outside `/ideation/*` (the Initiative
   *  rail's modal): tabs switch in state and the page is never navigated. */
  embedded: IEmbeddedIdeationScope | null
}

const IdeationScopeContext = createContext<IEmbeddedIdeationScope | null>(null)

interface IIdeationScopeProviderProps {
  ideationId: string
  initialTab?: TIdeationTab
  children: React.ReactNode
}

/** Hosts the ideation screens without their routes. */
export const IdeationScopeProvider: React.FC<IIdeationScopeProviderProps> = ({
  ideationId,
  initialTab = 'setup',
  children,
}) => {
  const [activeTab, setActiveTab] = useState<TIdeationTab>(initialTab)
  const value = useMemo(
    () => ({ideationId, activeTab, setActiveTab}),
    [ideationId, activeTab],
  )
  return (
    <IdeationScopeContext.Provider value={value}>
      {children}
    </IdeationScopeContext.Provider>
  )
}

/**
 * Which ideation the setup/results/insights screens are showing.
 *
 * On the `/ideation/*` routes there is no provider and the id is the bare
 * query string (`/ideation/setup?<id>`), exactly as before. Inside an
 * IdeationScopeProvider it comes from the provider instead.
 */
export const useIdeationScope = (): IIdeationScope => {
  const embedded = useContext(IdeationScopeContext)
  const {search} = useLocation()
  if (embedded) return {ideationId: embedded.ideationId, embedded}
  return {ideationId: search ? search.substring(1) : '', embedded: null}
}
